import React, { useState, useEffect, useCallback, useRef } from 'react';
import { generateId } from '../../utils/generateId';
import {
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Tooltip,
  InputBase,
  Select,
  FormControl,
  InputLabel,
  Collapse,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Description as IntroIcon,
  Language as WebIcon,
  DesktopWindows as DesktopIcon,
  Visibility as PreviewIcon,
  VisibilityOff as PreviewOffIcon,
  Settings as SettingsIcon,
  DragIndicator as DragIndicatorIcon,
  VisibilityOffOutlined as EyeSlashIcon,
  Check as CheckIcon,
  Circle as CircleIcon,
  ErrorOutline as ErrorIcon,
  OpenInNew as OpenNewIcon,
  Route as RouteIcon,
  MenuBook as MenuBookIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Share as ShareIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useNavigate, useParams } from 'react-router-dom';
import { workshopService } from '../../services/workshop.service';
import { stepService } from '../../services/step.service';
import { WorkshopDetail, Step, StepType, WorkshopCategory, WorkshopDifficulty, CompletionCriteria } from '../../types/workshop.types';
import { ContentBlock, BlockType } from '../../types/blocks';
import { useNotification } from '../../context/NotificationContext';
import { BlockEditor, BlockRenderer } from '../../components/workshop/blocks';

// -- Constants --

const statusConfig: Record<string, { label: string; color: 'default' | 'success' | 'warning' }> = {
  draft: { label: 'Draft', color: 'default' },
  published: { label: 'Published', color: 'success' },
  archived: { label: 'Archived', color: 'warning' },
};

const stepTypeConfig: Record<StepType, { label: string; icon: React.ReactElement; description: string }> = {
  introduction: { label: 'Introduction', icon: <IntroIcon />, description: 'No external app' },
  web_app: { label: 'Web App', icon: <WebIcon />, description: 'Embed via iframe' },
  remote_desktop: { label: 'Remote Desktop', icon: <DesktopIcon />, description: 'RDP via Guacamole' },
};

const completionOptions: { value: CompletionCriteria; label: string; description: string }[] = [
  { value: 'manual', label: 'Manual', description: 'Student clicks Mark Complete' },
  { value: 'checklist_gated', label: 'Checklist-gated', description: 'All tasks must be checked' },
  { value: 'auto_advance', label: 'Auto-advance', description: 'Advances when tasks done' },
];

const splitOptions = [25, 30, 35, 40, 45, 50];

const categoryOptions: { value: WorkshopCategory; label: string }[] = [
  { value: 'privilege_cloud', label: 'Privilege Cloud' },
  { value: 'identity_security', label: 'Identity Security' },
  { value: 'pam_self_hosted', label: 'PAM Self-Hosted' },
  { value: 'secrets_manager', label: 'Secrets Manager' },
  { value: 'endpoint_privilege_manager', label: 'Endpoint Privilege Manager' },
  { value: 'other', label: 'Other' },
];

const difficultyOptions: { value: WorkshopDifficulty; label: string }[] = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'expert', label: 'Expert' },
];

type SaveStatus = 'saved' | 'saving' | 'unsaved' | 'error';

// -- Step templates for "Add Step" picker --

interface StepTemplate {
  name: string;
  icon: React.ReactElement;
  description: string;
  blocks: ContentBlock[];
  completionCriteria: CompletionCriteria;
}

function makeBlocks(defs: { type: BlockType; content: string; meta: Record<string, any> }[]): ContentBlock[] {
  return defs.map((d, i) => ({
    id: generateId(),
    type: d.type,
    content: d.content,
    meta: d.meta,
    order: i,
  }));
}

const stepTemplates: StepTemplate[] = [
  {
    name: 'Guided Walkthrough',
    icon: <RouteIcon />,
    description: 'Step-by-step tasks with instructions and a checklist',
    blocks: makeBlocks([
      { type: 'heading', content: 'Overview', meta: { level: 2 } },
      { type: 'text', content: '', meta: {} },
      { type: 'callout', content: '', meta: { variant: 'info', title: 'Before you begin' } },
      { type: 'heading', content: 'Tasks', meta: { level: 2 } },
      {
        type: 'checklist',
        content: '',
        meta: {
          items: [
            { id: generateId(), text: 'Task 1' },
            { id: generateId(), text: 'Task 2' },
            { id: generateId(), text: 'Task 3' },
          ],
        },
      },
    ]),
    completionCriteria: 'checklist_gated',
  },
  {
    name: 'Reference / Learn',
    icon: <MenuBookIcon />,
    description: 'Explanatory content with concepts and examples',
    blocks: makeBlocks([
      { type: 'heading', content: 'Key Concepts', meta: { level: 2 } },
      { type: 'text', content: '', meta: {} },
      { type: 'code', content: '', meta: { language: 'plaintext' } },
      { type: 'callout', content: '', meta: { variant: 'tip', title: 'Pro tip' } },
    ]),
    completionCriteria: 'manual',
  },
  {
    name: 'Blank',
    icon: <AddIcon />,
    description: 'Start from scratch',
    blocks: [],
    completionCriteria: 'manual',
  },
];

// -- Sortable Step Card for the left sidebar --

interface SortableStepCardProps {
  step: Step;
  index: number;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  hideActions?: boolean;
}

function SortableStepCard({ step, index, isSelected, onSelect, onDelete, hideActions }: SortableStepCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: step.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      elevation={isDragging ? 8 : 0}
      onClick={onSelect}
      sx={{
        p: 1,
        mb: 0.75,
        cursor: 'pointer',
        borderLeft: 3,
        borderColor: isSelected ? 'primary.main' : 'divider',
        bgcolor: isSelected ? 'action.selected' : 'transparent',
        '&:hover': { bgcolor: isSelected ? 'action.selected' : 'action.hover' },
        '&:hover .step-actions': { opacity: 1 },
        position: 'relative',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box
          {...attributes}
          {...listeners}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          sx={{
            cursor: 'grab',
            display: 'flex',
            color: 'text.disabled',
            '&:hover': { color: 'text.secondary' },
            '&:active': { cursor: 'grabbing' },
            flexShrink: 0,
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 16 }} />
        </Box>
        <Typography
          variant="caption"
          sx={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            bgcolor: isSelected ? 'primary.main' : 'action.hover',
            color: isSelected ? '#fff' : 'text.secondary',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.6875rem',
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {index + 1}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: isSelected ? 600 : 400, fontSize: '0.8125rem' }}>
            {step.title || 'Untitled Step'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ color: 'text.disabled', display: 'flex', '& .MuiSvgIcon-root': { fontSize: 12 } }}>
              {stepTypeConfig[step.type]?.icon}
            </Box>
            {step.estimatedTime && (
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.625rem' }}>
                ~{step.estimatedTime}m
              </Typography>
            )}
          </Box>
        </Box>
        {!hideActions && (
          <IconButton
            className="step-actions"
            size="small"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            sx={{ opacity: 0, transition: 'opacity 0.15s', color: 'text.disabled', '&:hover': { color: 'error.main' } }}
          >
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>
    </Paper>
  );
}

// -- Settings Dialog Form --

interface SettingsForm {
  title: string;
  description: string;
  category: WorkshopCategory;
  difficulty: WorkshopDifficulty;
  estimatedDuration: number | string;
  maxStudents: number | string;
  prerequisites: string;
  learningObjectives: string;
  allowHelpRequests: boolean;
  enforceStepOrder: boolean;
}

// -- Main Component --

const WorkshopEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const showNotificationRef = useRef(showNotification);
  showNotificationRef.current = showNotification;

  // Core state
  const [workshop, setWorkshop] = useState<WorkshopDetail | null>(null);
  const [steps, setSteps] = useState<Step[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(true);

  // Editable step fields
  const [stepTitle, setStepTitle] = useState('');
  const [stepType, setStepType] = useState<StepType>('introduction');
  const [stepTargetUrl, setStepTargetUrl] = useState('');
  const [stepEstimatedTime, setStepEstimatedTime] = useState<number | string>('');
  const [stepBlocks, setStepBlocks] = useState<ContentBlock[]>([]);
  const [stepCompletionCriteria, setStepCompletionCriteria] = useState<CompletionCriteria>('manual');
  const [stepInstructorNotes, setStepInstructorNotes] = useState('');
  const [stepEnvironmentLabel, setStepEnvironmentLabel] = useState('');
  const [stepSplitRatio, setStepSplitRatio] = useState(35);
  const [notesExpanded, setNotesExpanded] = useState(false);

  // Workshop title (inline editable)
  const [workshopTitle, setWorkshopTitle] = useState('');

  // Save status
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved');
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSaveRef = useRef<Record<string, any> | null>(null);

  // Template picker
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);

  // Delete dialog
  const [deleteStepDialog, setDeleteStepDialog] = useState<string | null>(null);

  // Settings dialog
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState<SettingsForm | null>(null);
  const [settingsTagsInput, setSettingsTagsInput] = useState('');
  const [settingsTouched, setSettingsTouched] = useState<Record<string, boolean>>({});
  const [settingsSaving, setSettingsSaving] = useState(false);

  // Publish/Archive/Delete workshop dialogs
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [deleteWorkshopDialog, setDeleteWorkshopDialog] = useState(false);

  // Share/Unshare dialogs
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [unshareDialogOpen, setUnshareDialogOpen] = useState(false);
  const [sharing, setSharing] = useState(false);

  // ─── Data Loading ──────────────────────────────

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [ws, st] = await Promise.all([
        workshopService.getWorkshop(id!),
        stepService.getSteps(id!),
      ]);
      setWorkshop(ws);
      setWorkshopTitle(ws.title);
      setSteps(st);
    } catch {
      showNotificationRef.current('Failed to load tech eval', 'error');
      navigate('/instructor');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (id) loadData();
  }, [id, loadData]);

  // ─── Step Selection ────────────────────────────

  const selectStep = useCallback((step: Step) => {
    setSelectedStepId(step.id);
    setStepTitle(step.title);
    setStepType(step.type || 'introduction');
    setStepTargetUrl(step.targetUrl || '');
    setStepEstimatedTime(step.estimatedTime ?? '');
    setStepBlocks(step.blocks || []);
    setStepCompletionCriteria(step.completionCriteria || 'manual');
    setStepInstructorNotes(step.instructorNotes || '');
    setStepEnvironmentLabel(step.environmentLabel || '');
    setStepSplitRatio(step.splitRatio || 35);
    setNotesExpanded(false);
    setSaveStatus('saved');
  }, []);

  // ─── Auto-Save Logic ──────────────────────────

  const doSave = useCallback(async (stepId: string, payload: Record<string, any>) => {
    setSaveStatus('saving');
    try {
      await stepService.updateStep(stepId, payload);
      setSaveStatus('saved');
      // Refresh steps silently
      const updated = await stepService.getSteps(id!);
      setSteps(updated);
      const refreshedWorkshop = await workshopService.getWorkshop(id!);
      setWorkshop(refreshedWorkshop);
    } catch {
      setSaveStatus('error');
    }
  }, [id]);

  const scheduleSave = useCallback((payload: Record<string, any>) => {
    if (!selectedStepId || workshop?.status === 'archived') return;
    pendingSaveRef.current = payload;
    setSaveStatus('unsaved');
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      if (pendingSaveRef.current) {
        doSave(selectedStepId, pendingSaveRef.current);
        pendingSaveRef.current = null;
      }
    }, 1500);
  }, [selectedStepId, doSave, workshop?.status]);

  const flushSave = useCallback(async () => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    if (pendingSaveRef.current && selectedStepId) {
      await doSave(selectedStepId, pendingSaveRef.current);
      pendingSaveRef.current = null;
    }
  }, [selectedStepId, doSave]);

  // Save current step payload helper
  const getCurrentPayload = useCallback(() => ({
    title: stepTitle,
    type: stepType,
    targetUrl: stepTargetUrl || undefined,
    estimatedTime: stepEstimatedTime === '' ? undefined : Number(stepEstimatedTime) || undefined,
    blocks: stepBlocks,
    completionCriteria: stepCompletionCriteria,
    instructorNotes: stepInstructorNotes || undefined,
    environmentLabel: stepEnvironmentLabel || undefined,
    splitRatio: stepSplitRatio,
  }), [stepTitle, stepType, stepTargetUrl, stepEstimatedTime, stepBlocks, stepCompletionCriteria, stepInstructorNotes, stepEnvironmentLabel, stepSplitRatio]);

  // Auto-save on field changes
  useEffect(() => {
    if (!selectedStepId || loading) return;
    scheduleSave(getCurrentPayload());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepTitle, stepType, stepTargetUrl, stepEstimatedTime, stepBlocks, stepCompletionCriteria, stepInstructorNotes, stepEnvironmentLabel, stepSplitRatio]);

  // Ctrl+S handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        flushSave();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [flushSave]);

  // Save on beforeunload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (pendingSaveRef.current && selectedStepId) {
        // Attempt synchronous save via beacon — best effort
        const payload = pendingSaveRef.current;
        navigator.sendBeacon?.(
          `/api/steps/${selectedStepId}`,
          new Blob([JSON.stringify(payload)], { type: 'application/json' }),
        );
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [selectedStepId]);

  // Flush save on step switch
  const handleSelectStep = useCallback(async (step: Step) => {
    await flushSave();
    selectStep(step);
  }, [flushSave, selectStep]);

  // ─── Workshop Title Auto-Save ─────────────────

  const workshopTitleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleWorkshopTitleChange = (newTitle: string) => {
    setWorkshopTitle(newTitle);
    if (workshopTitleTimeoutRef.current) clearTimeout(workshopTitleTimeoutRef.current);
    workshopTitleTimeoutRef.current = setTimeout(async () => {
      if (newTitle.trim() && newTitle !== workshop?.title) {
        try {
          await workshopService.updateWorkshop(id!, { title: newTitle });
          setWorkshop((prev) => prev ? { ...prev, title: newTitle } : prev);
        } catch {
          showNotificationRef.current('Failed to save title', 'error');
        }
      }
    }, 1500);
  };

  // ─── Step DnD ──────────────────────────────────

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || workshop?.status === 'archived') return;
    const oldIndex = steps.findIndex((s) => s.id === active.id);
    const newIndex = steps.findIndex((s) => s.id === over.id);
    const reordered = arrayMove(steps, oldIndex, newIndex);
    setSteps(reordered);
    try {
      const payload = reordered.map((s, i) => ({ id: s.id, orderIndex: i }));
      await stepService.reorderSteps(id!, payload);
    } catch {
      showNotificationRef.current('Failed to reorder steps', 'error');
      const original = await stepService.getSteps(id!);
      setSteps(original);
    }
  };

  // ─── Add Step (from template) ──────────────────

  const handleAddStep = async (template: StepTemplate) => {
    setTemplatePickerOpen(false);
    try {
      // Re-generate block IDs for fresh step
      const blocks = template.blocks.map((b, i) => ({
        ...b,
        id: generateId(),
        order: i,
        meta: b.type === 'checklist' && b.meta?.items
          ? { ...b.meta, items: (b.meta.items as any[]).map((item: any) => ({ ...item, id: generateId() })) }
          : { ...b.meta },
      }));
      const created = await stepService.createStep(id!, {
        title: 'New Step',
        content: '',
        blocks,
        completionCriteria: template.completionCriteria,
      });
      const [updatedSteps, updatedWorkshop] = await Promise.all([
        stepService.getSteps(id!),
        workshopService.getWorkshop(id!),
      ]);
      setSteps(updatedSteps);
      setWorkshop(updatedWorkshop);
      // Select the new step
      const newStep = updatedSteps.find((s) => s.id === created.id);
      if (newStep) selectStep(newStep);
      showNotificationRef.current('Step created', 'success');
    } catch {
      showNotificationRef.current('Failed to create step', 'error');
    }
  };

  // ─── Delete Step ───────────────────────────────

  const handleDeleteStep = async (stepId: string) => {
    setDeleteStepDialog(null);
    try {
      await stepService.deleteStep(stepId);
      if (selectedStepId === stepId) {
        setSelectedStepId(null);
      }
      const [updatedSteps, updatedWorkshop] = await Promise.all([
        stepService.getSteps(id!),
        workshopService.getWorkshop(id!),
      ]);
      setSteps(updatedSteps);
      setWorkshop(updatedWorkshop);
      showNotificationRef.current('Step deleted', 'success');
    } catch {
      showNotificationRef.current('Failed to delete step', 'error');
    }
  };

  // ─── Workshop Actions ──────────────────────────

  const handlePublish = async () => {
    setPublishDialogOpen(false);
    try {
      await workshopService.updateWorkshop(id!, { status: 'published' });
      showNotificationRef.current('Tech Eval published!', 'success');
      await loadData();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to publish tech eval';
      showNotificationRef.current(msg, 'error');
    }
  };

  const handleArchive = async () => {
    setArchiveDialogOpen(false);
    try {
      await workshopService.updateWorkshop(id!, { status: 'archived' });
      showNotificationRef.current('Tech Eval archived', 'success');
      await loadData();
    } catch {
      showNotificationRef.current('Failed to archive tech eval', 'error');
    }
  };

  const handleUnarchiveWorkshop = async () => {
    if (!window.confirm('This will return the tech eval to Draft status. You will need to publish it again to make it available for sessions. Continue?')) return;
    try {
      await workshopService.updateWorkshop(id!, { status: 'draft' });
      showNotificationRef.current('Tech Eval unarchived. It is now in Draft status.', 'success');
      await loadData();
    } catch (err: any) {
      showNotificationRef.current(err?.response?.data?.message || 'Failed to unarchive', 'error');
    }
  };

  const handleDeleteWorkshop = async () => {
    setDeleteWorkshopDialog(false);
    try {
      await workshopService.deleteWorkshop(id!);
      showNotificationRef.current('Tech Eval deleted', 'success');
      navigate('/instructor');
    } catch {
      showNotificationRef.current('Failed to delete tech eval', 'error');
    }
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      const updated = await workshopService.shareToLibrary(workshop!.id);
      setWorkshop((prev) => prev ? { ...prev, sharedToLibrary: updated.sharedToLibrary } : prev);
      showNotificationRef.current('Shared to library', 'success');
      setShareDialogOpen(false);
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to share to library';
      showNotificationRef.current(msg, 'error');
      setShareDialogOpen(false);
    } finally {
      setSharing(false);
    }
  };

  const handleUnshare = async () => {
    setSharing(true);
    try {
      const updated = await workshopService.unshareFromLibrary(workshop!.id);
      setWorkshop((prev) => prev ? { ...prev, sharedToLibrary: updated.sharedToLibrary } : prev);
      showNotificationRef.current('Removed from library', 'success');
      setUnshareDialogOpen(false);
    } catch (err: any) {
      showNotificationRef.current('Failed to remove from library', 'error');
      setUnshareDialogOpen(false);
    } finally {
      setSharing(false);
    }
  };

  // ─── Settings Dialog ───────────────────────────

  const openSettings = () => {
    if (!workshop) return;
    setSettingsForm({
      title: workshop.title,
      description: workshop.description || '',
      category: workshop.category || 'other',
      difficulty: workshop.difficulty || 'beginner',
      estimatedDuration: workshop.estimatedDuration ?? 45,
      maxStudents: workshop.maxStudents ?? 20,
      prerequisites: workshop.prerequisites || '',
      learningObjectives: workshop.learningObjectives || '',
      allowHelpRequests: workshop.allowHelpRequests ?? true,
      enforceStepOrder: workshop.enforceStepOrder ?? true,
    });
    setSettingsTagsInput(workshop.tags?.join(', ') || '');
    setSettingsTouched({});
    setSettingsOpen(true);
  };

  const updateSettingsField = <K extends keyof SettingsForm>(field: K, value: SettingsForm[K]) => {
    setSettingsForm((prev) => prev ? { ...prev, [field]: value } : prev);
  };

  const handleSettingsNumberChange = (field: 'estimatedDuration' | 'maxStudents', value: string) => {
    if (value === '') { updateSettingsField(field, ''); return; }
    const parsed = parseInt(value, 10);
    if (!isNaN(parsed)) updateSettingsField(field, parsed);
  };

  const handleSaveSettings = async () => {
    if (!settingsForm || !settingsForm.title.trim()) {
      setSettingsTouched((prev) => ({ ...prev, title: true }));
      showNotification('Title is required', 'error');
      return;
    }
    setSettingsSaving(true);
    try {
      await workshopService.updateWorkshop(id!, {
        title: settingsForm.title,
        description: settingsForm.description,
        category: settingsForm.category,
        difficulty: settingsForm.difficulty,
        maxStudents: Number(settingsForm.maxStudents) || 20,
        tags: settingsTagsInput ? settingsTagsInput.split(',').map((t) => t.trim()).filter(Boolean) : [],
        prerequisites: settingsForm.prerequisites,
        learningObjectives: settingsForm.learningObjectives,
        allowHelpRequests: settingsForm.allowHelpRequests,
        enforceStepOrder: settingsForm.enforceStepOrder,
      });
      showNotification('Tech Eval settings saved', 'success');
      setSettingsOpen(false);
      await loadData();
    } catch {
      showNotification('Failed to save settings', 'error');
    } finally {
      setSettingsSaving(false);
    }
  };

  // ─── Loading State ─────────────────────────────

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!workshop) return null;

  const isArchived = workshop.status === 'archived';
  const isDraft = workshop.status === 'draft';
  const isPublished = workshop.status === 'published';
  const totalEstimatedTime = steps.reduce((sum, s) => sum + (s.estimatedTime || 0), 0);
  const showEnvironmentBar = stepType === 'web_app' || stepType === 'remote_desktop';

  // ─── Render ────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'background.default' }}>
      {/* ═══ Studio Header ═══ */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          gap: 1.5,
          flexShrink: 0,
          height: 56,
        }}
      >
        <Button
          startIcon={<BackIcon />}
          onClick={async () => { await flushSave(); navigate('/instructor'); }}
          size="small"
          sx={{ color: 'text.secondary', flexShrink: 0 }}
        >
          Back
        </Button>
        <InputBase
          value={workshopTitle}
          onChange={(e) => handleWorkshopTitleChange(e.target.value)}
          disabled={isArchived}
          sx={{
            fontSize: '1rem',
            fontWeight: 600,
            flex: 1,
            px: 1,
            py: 0.25,
            borderRadius: 1,
            border: '1px solid transparent',
            '&:hover': { borderColor: 'divider' },
            '&.Mui-focused': { borderColor: 'primary.main' },
            minWidth: 120,
          }}
          inputProps={{ 'aria-label': 'Tech Eval title' }}
        />
        <Chip
          label={statusConfig[workshop.status]?.label || workshop.status}
          color={statusConfig[workshop.status]?.color || 'default'}
          size="small"
        />
        {/* Save status */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 130, flexShrink: 0 }}>
          {saveStatus === 'saved' && (
            <>
              <CheckIcon sx={{ fontSize: 14, color: 'success.main' }} />
              <Typography variant="caption" color="text.secondary">All changes saved</Typography>
            </>
          )}
          {saveStatus === 'saving' && (
            <>
              <CircularProgress size={12} />
              <Typography variant="caption" color="text.secondary">Saving...</Typography>
            </>
          )}
          {saveStatus === 'unsaved' && (
            <>
              <CircleIcon sx={{ fontSize: 8, color: 'warning.main' }} />
              <Typography variant="caption" color="warning.main">Unsaved changes</Typography>
            </>
          )}
          {saveStatus === 'error' && (
            <Tooltip title="Click to retry">
              <Button size="small" onClick={flushSave} sx={{ textTransform: 'none', fontSize: '0.75rem', color: 'error.main', minWidth: 'auto' }}>
                <ErrorIcon sx={{ fontSize: 14, mr: 0.5 }} />
                Save failed
              </Button>
            </Tooltip>
          )}
        </Box>
        {!isArchived && (
          <Tooltip title="Tech Eval Settings">
            <IconButton onClick={openSettings} size="small">
              <SettingsIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={showPreview ? 'Hide preview' : 'Show preview'}>
          <IconButton onClick={() => setShowPreview(!showPreview)} size="small">
            {showPreview ? <PreviewOffIcon sx={{ fontSize: 20 }} /> : <PreviewIcon sx={{ fontSize: 20 }} />}
          </IconButton>
        </Tooltip>
        {isDraft && (
          <>
            <Button size="small" color="error" onClick={() => setDeleteWorkshopDialog(true)}>Delete</Button>
            <Button size="small" variant="contained" color="success" onClick={() => setPublishDialogOpen(true)}>Publish</Button>
          </>
        )}
        {isPublished && (
          <>
            {!workshop.sharedToLibrary ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<ShareIcon />}
                onClick={() => setShareDialogOpen(true)}
              >
                Share to Library
              </Button>
            ) : (
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={() => setUnshareDialogOpen(true)}
              >
                Remove from Library
              </Button>
            )}
            <Button size="small" variant="outlined" color="warning" onClick={() => setArchiveDialogOpen(true)}>Archive</Button>
          </>
        )}
        {isArchived && (
          <Button size="small" color="error" onClick={() => setDeleteWorkshopDialog(true)}>Delete</Button>
        )}
      </Box>

      {/* Archive Banner */}
      {isArchived && (
        <Paper
          elevation={0}
          sx={{
            px: 2,
            py: 1.5,
            mx: 2,
            mt: 1,
            bgcolor: '#fff3e0',
            border: '1px solid #ffe0b2',
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ArchiveIcon sx={{ color: '#e65100', fontSize: 20 }} />
            <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 500 }}>
              This tech eval is archived. Unarchive to make changes.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<UnarchiveIcon />}
            onClick={handleUnarchiveWorkshop}
            sx={{ borderColor: '#e65100', color: '#e65100', '&:hover': { borderColor: '#bf360c', bgcolor: '#fff3e0' } }}
          >
            Unarchive
          </Button>
        </Paper>
      )}

      {/* ═══ Three-Panel Layout ═══ */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* ── Left: Step List Sidebar ── */}
        <Box
          sx={{
            width: 240,
            minWidth: 240,
            borderRight: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ p: 1.5 }}>
            {!isArchived && (
              <Button
                fullWidth
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setTemplatePickerOpen(true)}
              >
                Add Step
              </Button>
            )}
          </Box>
          <Divider />
          <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
            {steps.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4, fontSize: '0.75rem' }}>
                No steps yet. Click "Add Step" to begin.
              </Typography>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={steps.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                  {steps.map((step, index) => (
                    <SortableStepCard
                      key={step.id}
                      step={step}
                      index={index}
                      isSelected={selectedStepId === step.id}
                      onSelect={() => handleSelectStep(step)}
                      onDelete={() => setDeleteStepDialog(step.id)}
                      hideActions={isArchived}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </Box>
          <Divider />
          <Box sx={{ p: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem' }}>
              {steps.length} step{steps.length !== 1 ? 's' : ''}
              {totalEstimatedTime > 0 && ` \u00B7 ~${totalEstimatedTime} min`}
            </Typography>
          </Box>
        </Box>

        {/* ── Center: Step Editor ── */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          {!selectedStepId ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
              <Typography color="text.secondary" sx={{ mb: 1 }}>
                Select a step to edit, or add your first step
              </Typography>
              {!isArchived && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {stepTemplates.map((tmpl) => (
                    <Paper
                      key={tmpl.name}
                      elevation={0}
                      onClick={() => handleAddStep(tmpl)}
                      sx={{
                        p: 2.5,
                        textAlign: 'center',
                        cursor: 'pointer',
                        width: 160,
                        '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                        transition: 'all 0.15s',
                      }}
                    >
                      <Box sx={{ color: 'primary.main', mb: 1 }}>{tmpl.icon}</Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{tmpl.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{tmpl.description}</Typography>
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ maxWidth: 800, mx: 'auto' }}>
              {/* Step Title */}
              <TextField
                fullWidth
                placeholder="Step title"
                value={stepTitle}
                onChange={(e) => setStepTitle(e.target.value)}
                disabled={isArchived}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: { fontSize: '1.25rem', fontWeight: 600, mb: 2 },
                }}
              />

              {/* Step Type Selector */}
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block', fontWeight: 500 }}>
                Step Type
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5 }}>
                {(Object.entries(stepTypeConfig) as [StepType, typeof stepTypeConfig[StepType]][]).map(([type, config]) => (
                  <Paper
                    key={type}
                    elevation={0}
                    onClick={() => !isArchived && setStepType(type)}
                    sx={{
                      flex: 1,
                      p: 1.5,
                      textAlign: 'center',
                      cursor: isArchived ? 'default' : 'pointer',
                      border: 2,
                      borderColor: stepType === type ? 'primary.main' : 'divider',
                      bgcolor: stepType === type ? 'action.hover' : 'transparent',
                      transition: 'all 0.15s',
                      ...(!isArchived && { '&:hover': { borderColor: 'primary.light' } }),
                    }}
                  >
                    <Box sx={{ color: stepType === type ? 'primary.main' : 'text.secondary', mb: 0.25 }}>
                      {config.icon}
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: stepType === type ? 600 : 400, fontSize: '0.8125rem' }}>
                      {config.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem' }}>
                      {config.description}
                    </Typography>
                  </Paper>
                ))}
              </Box>

              {/* Completion Criteria + Estimated Time */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2.5, alignItems: 'flex-start' }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Completion</InputLabel>
                  <Select
                    value={stepCompletionCriteria}
                    label="Completion"
                    onChange={(e) => setStepCompletionCriteria(e.target.value as CompletionCriteria)}
                    disabled={isArchived}
                  >
                    {completionOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        <Box>
                          <Typography variant="body2">{opt.label}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.625rem' }}>
                            {opt.description}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Est. time (min)"
                  type="number"
                  value={stepEstimatedTime}
                  onChange={(e) => {
                    if (e.target.value === '') setStepEstimatedTime('');
                    else {
                      const val = parseInt(e.target.value, 10);
                      if (!isNaN(val)) setStepEstimatedTime(val);
                    }
                  }}
                  inputProps={{ min: 1, max: 120 }}
                  disabled={isArchived}
                  sx={{ width: 130 }}
                />
              </Box>

              {/* Environment Bar (conditional) */}
              {showEnvironmentBar && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 2.5,
                    bgcolor: 'background.default',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, mb: 1.5, display: 'block', color: 'text.secondary' }}>
                    Environment Configuration
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 1 }}>
                    <TextField
                      fullWidth
                      label={stepType === 'remote_desktop' ? 'Remote Desktop URL' : 'Application URL'}
                      placeholder="https://..."
                      value={stepTargetUrl}
                      onChange={(e) => setStepTargetUrl(e.target.value)}
                      disabled={isArchived}
                    />
                    <Tooltip title="Open in new tab">
                      <span>
                        <IconButton
                          size="small"
                          disabled={!stepTargetUrl}
                          onClick={() => window.open(stepTargetUrl, '_blank')}
                          sx={{ mt: 0.5 }}
                        >
                          <OpenNewIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <TextField
                      fullWidth
                      label="Environment Label"
                      placeholder="e.g., Privilege Cloud Portal"
                      value={stepEnvironmentLabel}
                      onChange={(e) => setStepEnvironmentLabel(e.target.value)}
                      disabled={isArchived}
                    />
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <InputLabel>Split</InputLabel>
                      <Select
                        value={stepSplitRatio}
                        label="Split"
                        onChange={(e) => setStepSplitRatio(Number(e.target.value))}
                        disabled={isArchived}
                      >
                        {splitOptions.map((val) => (
                          <MenuItem key={val} value={val}>{val}%</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Paper>
              )}

              {/* Content Blocks */}
              <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block', color: 'text.secondary' }}>
                Content Blocks
              </Typography>
              <Box sx={{ mb: 2.5, ...(isArchived && { pointerEvents: 'none', opacity: 0.7 }) }}>
                <BlockEditor blocks={stepBlocks} onChange={setStepBlocks} />
              </Box>

              {/* Instructor Notes */}
              <Divider sx={{ mb: 1.5 }} />
              <Box
                onClick={() => setNotesExpanded(!notesExpanded)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  py: 0.5,
                  '&:hover': { color: 'primary.main' },
                }}
              >
                <EyeSlashIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
                  Instructor Notes
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.625rem' }}>
                  Not visible to students
                </Typography>
                <Box sx={{ flex: 1 }} />
                {notesExpanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
              </Box>
              <Collapse in={notesExpanded}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  placeholder="Add notes for instructors (not visible to students)..."
                  value={stepInstructorNotes}
                  onChange={(e) => setStepInstructorNotes(e.target.value)}
                  disabled={isArchived}
                  sx={{ mt: 1 }}
                />
              </Collapse>
            </Box>
          )}
        </Box>

        {/* ── Right: Student Preview ── */}
        {showPreview && (
          <Box
            sx={{
              width: '35%',
              minWidth: 300,
              maxWidth: 450,
              borderLeft: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Preview header */}
            <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Student Preview</Typography>
              <Chip label="Live" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.625rem' }} />
            </Box>

            {!selectedStepId ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                <Typography variant="body2" color="text.secondary">Preview will appear here</Typography>
              </Box>
            ) : (
              <>
                {/* Environment banner */}
                {showEnvironmentBar && (
                  <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="caption" sx={{ fontSize: '0.6875rem' }}>
                      {stepType === 'web_app' ? '\uD83C\uDF10' : '\uD83D\uDDA5\uFE0F'}{' '}
                      Alongside: {stepEnvironmentLabel || stepTargetUrl || (stepType === 'remote_desktop' ? 'Remote Desktop' : 'Web App')}
                    </Typography>
                  </Box>
                )}

                {/* Preview content */}
                <Box
                  sx={{
                    flex: 1,
                    overflow: 'auto',
                    p: 2.5,
                    maxWidth: Math.round(1200 * (stepSplitRatio / 100)),
                  }}
                >
                  <BlockRenderer blocks={stepBlocks} />
                </Box>

                {/* Preview action buttons */}
                <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between' }}>
                  <Button size="small" variant="outlined" disabled sx={{ textTransform: 'none' }}>
                    \u2190 Previous
                  </Button>
                  <Tooltip
                    title={
                      stepCompletionCriteria === 'checklist_gated'
                        ? 'Activates when all tasks complete'
                        : stepCompletionCriteria === 'auto_advance'
                          ? 'Auto-advances when tasks done'
                          : ''
                    }
                  >
                    <span>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={stepCompletionCriteria === 'checklist_gated'}
                        sx={{ textTransform: 'none' }}
                      >
                        {stepCompletionCriteria === 'auto_advance' ? 'Auto-advance' : '\u2713 Mark Complete'}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </>
            )}
          </Box>
        )}
      </Box>

      {/* ═══ Dialogs ═══ */}

      {/* Template Picker */}
      <Dialog open={templatePickerOpen} onClose={() => setTemplatePickerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add a New Step</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, pt: 1 }}>
            {stepTemplates.map((tmpl) => (
              <Paper
                key={tmpl.name}
                elevation={0}
                onClick={() => handleAddStep(tmpl)}
                sx={{
                  flex: 1,
                  p: 2.5,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                  transition: 'all 0.15s',
                }}
              >
                <Box sx={{ color: 'primary.main', mb: 1 }}>{tmpl.icon}</Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{tmpl.name}</Typography>
                <Typography variant="caption" color="text.secondary">{tmpl.description}</Typography>
              </Paper>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplatePickerOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Step Dialog */}
      <Dialog open={!!deleteStepDialog} onClose={() => setDeleteStepDialog(null)}>
        <DialogTitle>Delete Step</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete this step. Remaining steps will be reindexed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteStepDialog(null)}>Cancel</Button>
          <Button onClick={() => deleteStepDialog && handleDeleteStep(deleteStepDialog)} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish Dialog */}
      <Dialog open={publishDialogOpen} onClose={() => setPublishDialogOpen(false)}>
        <DialogTitle>Publish Tech Eval</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Once published, this tech eval can be used in sessions. You can still edit the content.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishDialogOpen(false)}>Cancel</Button>
          <Button onClick={handlePublish} color="success" variant="contained">Publish</Button>
        </DialogActions>
      </Dialog>

      {/* Archive Dialog */}
      <Dialog open={archiveDialogOpen} onClose={() => setArchiveDialogOpen(false)}>
        <DialogTitle>Archive Tech Eval</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Archiving this tech eval will prevent it from being used in new sessions.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleArchive} color="warning" variant="contained">Archive</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Workshop Dialog */}
      <Dialog open={deleteWorkshopDialog} onClose={() => setDeleteWorkshopDialog(false)}>
        <DialogTitle>Delete Tech Eval</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete this tech eval and all its steps. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteWorkshopDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteWorkshop} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Share to Library Dialog */}
      <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)}>
        <DialogTitle>Share to Library</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Share &quot;{workshop?.title}&quot; to the shared library? All instructors will be able to clone and use this tech eval.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialogOpen(false)} disabled={sharing}>Cancel</Button>
          <Button onClick={handleShare} variant="contained" disabled={sharing}>
            {sharing ? 'Sharing...' : 'Share'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unshare from Library Dialog */}
      <Dialog open={unshareDialogOpen} onClose={() => setUnshareDialogOpen(false)}>
        <DialogTitle>Remove from Library</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove from shared library? Other instructors won&apos;t be able to find or clone this tech eval anymore.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnshareDialogOpen(false)} disabled={sharing}>Cancel</Button>
          <Button onClick={handleUnshare} variant="outlined" color="warning" disabled={sharing}>
            {sharing ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Workshop Settings Dialog */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Tech Eval Settings</DialogTitle>
        <DialogContent dividers>
          {settingsForm && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Basic Information</Typography>
              <Divider sx={{ mb: 2 }} />
              <TextField
                fullWidth
                label="Title"
                required
                value={settingsForm.title}
                onChange={(e) => updateSettingsField('title', e.target.value)}
                onBlur={() => setSettingsTouched((prev) => ({ ...prev, title: true }))}
                error={settingsTouched.title && !settingsForm.title?.trim()}
                helperText={settingsTouched.title && !settingsForm.title?.trim() ? 'Title is required' : ''}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Description"
                multiline
                minRows={3}
                value={settingsForm.description}
                onChange={(e) => updateSettingsField('description', e.target.value)}
                sx={{ mb: 3 }}
              />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>Tech Eval Configuration</Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="Category"
                    value={settingsForm.category}
                    onChange={(e) => updateSettingsField('category', e.target.value as WorkshopCategory)}
                  >
                    {categoryOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="Difficulty"
                    value={settingsForm.difficulty}
                    onChange={(e) => updateSettingsField('difficulty', e.target.value as WorkshopDifficulty)}
                  >
                    {difficultyOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Estimated Duration
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {Number(settingsForm.estimatedDuration) > 0
                      ? `${settingsForm.estimatedDuration} minutes (calculated from steps)`
                      : 'Add step times to calculate'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Max Students"
                    type="number"
                    value={settingsForm.maxStudents}
                    onChange={(e) => handleSettingsNumberChange('maxStudents', e.target.value)}
                    inputProps={{ min: 1, max: 100 }}
                  />
                </Grid>
              </Grid>
              <TextField
                fullWidth
                label="Tags"
                placeholder="e.g., privilege-cloud, safes, security"
                helperText="Comma-separated tags"
                value={settingsTagsInput}
                onChange={(e) => setSettingsTagsInput(e.target.value)}
                sx={{ mb: 3 }}
              />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>Learning Details</Typography>
              <Divider sx={{ mb: 2 }} />
              <TextField
                fullWidth
                label="Prerequisites"
                multiline
                minRows={2}
                value={settingsForm.prerequisites}
                onChange={(e) => updateSettingsField('prerequisites', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Learning Objectives"
                multiline
                minRows={2}
                value={settingsForm.learningObjectives}
                onChange={(e) => updateSettingsField('learningObjectives', e.target.value)}
                sx={{ mb: 3 }}
              />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>Behavior</Typography>
              <Divider sx={{ mb: 2 }} />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settingsForm.allowHelpRequests}
                    onChange={(e) => updateSettingsField('allowHelpRequests', e.target.checked)}
                  />
                }
                label="Enable help requests during tech eval"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settingsForm.enforceStepOrder}
                    onChange={(e) => updateSettingsField('enforceStepOrder', e.target.checked)}
                  />
                }
                label="Enforce step order (students must complete steps sequentially)"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveSettings}
            variant="contained"
            disabled={settingsSaving || !settingsForm?.title?.trim()}
          >
            {settingsSaving ? <CircularProgress size={24} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkshopEditor;
